import React, { useState } from 'react';
import { Box, Chip, Popover, Stack, Typography } from '@mui/material';
import type { MetricQualification, StatTier } from '../types';
import { STAT_TIER_TEST_IDS } from '../testIds';
import { STAT_TIER_COPY, formatPct, formatTierChipLabel, formatTierChipVerdict } from '../statTierCopy';

/**
 * Spec D D1 (§7.2.2): the three-state qualifier every conversion number
 * carries. Render-only — every figure comes from the server-computed
 * `qualification` (AC-D1-7); this component NEVER does statistics.
 *
 * Follows the QualitySeverityChip idiom (outlined small chip, theme colors).
 * Click opens a Popover with the evidence (n, CI, method, SRM) — jargon
 * lives one click deep. Copy table + label derivation live in
 * `../statTierCopy` (node-safe, unit-tested); re-exported here for consumers.
 */

export { STAT_TIER_COPY, formatTierChipLabel, formatTierChipVerdict };

const TIER_CHIP_COLOR: Record<StatTier, 'success' | 'warning' | 'default'> = {
  solid: 'success',
  directional: 'warning',
  insufficient: 'default',
};

export interface StatTierChipProps {
  qualification: MetricQualification;
  /** Override the default registry testid when a surface renders many chips. */
  testId?: string;
  /**
   * `verdict` drops the rate from the label for a surface that already prints
   * it next to the chip (the Reports gates band). The popover is unchanged, so
   * n / CI / method stay one click away either way.
   */
  label?: 'full' | 'verdict';
}

export const StatTierChip: React.FC<StatTierChipProps> = ({ qualification, testId, label = 'full' }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { tier, n, p, ciLow, ciHigh, srmOk, neededN } = qualification;

  return (
    <>
      <Chip
        label={label === 'verdict' ? formatTierChipVerdict(qualification) : formatTierChipLabel(qualification)}
        color={TIER_CHIP_COLOR[tier]}
        size="small"
        variant="outlined"
        data-testid={testId ?? STAT_TIER_TEST_IDS.chip}
        data-tier={tier}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        sx={{ cursor: 'pointer' }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-testid={STAT_TIER_TEST_IDS.popover}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {STAT_TIER_COPY[tier].icon}{' '}
            {tier === 'solid' ? 'Solid estimate' : tier === 'directional' ? 'Directional estimate' : 'Insufficient sample'}
          </Typography>
          <Stack spacing={0.5}>
            <PopoverRow label="Observed" value={`${formatPct(p)} of ${n.toLocaleString('en-US')}`} />
            <PopoverRow label="95% CI" value={`${formatPct(ciLow)} – ${formatPct(ciHigh)}`} />
            <PopoverRow label="Method" value="Wilson score interval" />
            {srmOk !== undefined && (
              <PopoverRow label="Traffic split" value={srmOk ? 'clean (SRM check passed)' : 'FAILED the SRM check'} />
            )}
            {neededN !== undefined && neededN > 0 && (
              <PopoverRow label="Needed" value={`~${neededN.toLocaleString('en-US')} more for a solid read`} />
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1, pt: 1, borderTop: theme => `1px solid ${theme.palette.divider}` }}
          >
            {STAT_TIER_COPY[tier].meaning}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

/** Multiplicity-rule marker (§7.2.3) for breakdown rows — label only, no math. */
export const ExploratoryLabel: React.FC = () => (
  <Chip
    label="exploratory"
    size="small"
    variant="outlined"
    data-testid={STAT_TIER_TEST_IDS.exploratoryLabel}
    sx={{ fontStyle: 'italic', color: 'text.secondary', height: 20, fontSize: '0.6875rem' }}
  />
);

function PopoverRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Stack direction="row" spacing={1} justifyContent="space-between">
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
    </Stack>
  );
}

